// src/pages/lms/LMSCourses.jsx - Complete course management portal
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import {
  Plus, X, Trash2, Upload, FileText, Film, Image,
  Music, File, Link, Eye, ChevronRight, ChevronDown,
  Edit, BookOpen, Search, CheckCircle, AlertCircle,
  GripVertical, ExternalLink,
} from 'lucide-react';

// File type detection
const getFileType = (name, mime) => {
  const ext  = name.split('.').pop().toLowerCase();
  const pdf   = ['pdf'];
  const video = ['mp4','mov','avi','mkv','webm','m4v'];
  const image = ['jpg','jpeg','png','gif','webp','svg','bmp'];
  const doc   = ['doc','docx','txt','rtf'];
  const ppt   = ['ppt','pptx'];
  const excel = ['xls','xlsx','csv'];
  const audio = ['mp3','wav','ogg','m4a','aac'];
  if (pdf.includes(ext))   return 'pdf';
  if (video.includes(ext)) return 'video';
  if (image.includes(ext)) return 'image';
  if (doc.includes(ext))   return 'doc';
  if (ppt.includes(ext))   return 'ppt';
  if (excel.includes(ext)) return 'excel';
  if (audio.includes(ext)) return 'audio';
  return 'other';
};

const TYPE_ICONS = {
  pdf:   <FileText size={18} color="#EF4444" />,
  video: <Film     size={18} color="#7C3AED" />,
  image: <Image    size={18} color="#10B981" />,
  doc:   <FileText size={18} color="#4F8EF7" />,
  ppt:   <FileText size={18} color="#F59E0B" />,
  excel: <FileText size={18} color="#10B981" />,
  audio: <Music    size={18} color="#EC4899" />,
  link:  <Link     size={18} color="#22D3EE" />,
  other: <File     size={18} color="#94A3B8" />,
};

const TYPE_COLORS = {
  pdf:'#EF4444', video:'#7C3AED', image:'#10B981',
  doc:'#4F8EF7', ppt:'#F59E0B',  excel:'#10B981',
  audio:'#EC4899', link:'#22D3EE', other:'#94A3B8',
};

const COVER_COLORS = ['#4F8EF7','#7C3AED','#10B981','#F59E0B','#EF4444','#EC4899','#22D3EE','#8B5CF6','#F97316'];

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)}KB`;
  return `${(bytes/1048576).toFixed(1)}MB`;
};

export default function LMSCourses() {
  const { account, institution } = useLMSAuth();
  const [courses,   setCourses]   = useState([]);
  const [teachers,  setTeachers]  = useState([]);
  const [classes,   setClasses]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [view,      setView]      = useState('list'); // 'list' | 'detail'
  const [selCourse, setSelCourse] = useState(null);
  const [chapters,  setChapters]  = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showChapterForm,setShowChapterForm]= useState(false);
  const [showMaterialForm,setShowMaterialForm]=useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [editChapter,setEditChapter]= useState(null);
  const [selChapter, setSelChapter] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const fileRef = useRef();
  const thumbRef= useRef();

  const emptyCourse = {
    title:'', description:'', subject:'', subject_code:'',
    teacher_id:'', target_degree:'all', target_year:'',
    target_department:'', target_class_id:'',
    cover_color:'#4F8EF7', thumbnail_url:'', is_published:true, is_featured:false,
  };
  const emptyChapter = { title:'', description:'', order_index:0, is_published:true };
  const emptyMaterial = {
    title:'', description:'', file_url:'', file_name:'', file_size:0,
    file_type:'', material_type:'other', mime_type:'',
    is_published:true, is_downloadable:true, external_url:'', order_index:0,
  };
  const [courseForm,  setCourseForm]  = useState(emptyCourse);
  const [chapterForm, setChapterForm] = useState(emptyChapter);
  const [materialForm,setMaterialForm]= useState(emptyMaterial);
  const [addingLink,  setAddingLink]  = useState(false);

  useEffect(() => { if (institution?.id) loadInit(); }, [institution?.id]);

  const loadInit = async () => {
    setLoading(true);
    try {
      const [cRes, tRes, clRes] = await Promise.all([
        supabase.from('lms_courses').select('*')
          .eq('institution_id', institution.id).order('created_at', { ascending:false }),
        supabase.from('lms_teachers').select('id,full_name,department')
          .eq('institution_id', institution.id).eq('status','active').order('full_name'),
        supabase.from('lms_classes').select('id,name')
          .eq('institution_id', institution.id).eq('is_active', true).order('name'),
      ]);
      setCourses(cRes.data || []);
      setTeachers(tRes.data || []);
      setClasses(clRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadCourseDetail = async (course) => {
    setSelCourse(course);
    setView('detail');
    try {
      const [chRes, mRes] = await Promise.all([
        supabase.from('lms_course_chapters').select('*')
          .eq('course_id', course.id).order('order_index'),
        supabase.from('lms_course_materials').select('*')
          .eq('course_id', course.id).order('chapter_id').order('order_index'),
      ]);
      setChapters(chRes.data || []);
      setMaterials(mRes.data || []);
    } catch(e) { console.error(e); }
  };

  // Upload thumbnail
  const uploadThumbnail = async (file) => {
    try {
      const path = `${institution.id}/thumbnails/${Date.now()}_${file.name}`;
      await supabase.storage.from('lms-course-thumbnails').upload(path, file, { upsert:true });
      const { data: { publicUrl } } = supabase.storage.from('lms-course-thumbnails').getPublicUrl(path);
      setCourseForm(f => ({ ...f, thumbnail_url: publicUrl }));
    } catch(e) { setError('Thumbnail upload failed'); }
  };

  // Upload material file
  const uploadMaterialFile = async (file) => {
    setUploading(true);
    setUploadPct(0);
    setError('');
    try {
      const mType = getFileType(file.name, file.type);
      const path  = `${institution.id}/courses/${Date.now()}_${file.name}`;
      const { error: upErr } = await supabase.storage
        .from('lms-courses')
        .upload(path, file, { upsert:true });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from('lms-courses').getPublicUrl(path);
      setMaterialForm(f => ({
        ...f,
        file_url:      publicUrl,
        file_name:     file.name,
        file_size:     file.size,
        file_type:     file.name.split('.').pop().toLowerCase(),
        material_type: mType,
        mime_type:     file.type,
        title:         f.title || file.name.replace(/\.[^/.]+$/, ''),
      }));
      setUploadPct(100);
    } catch(e) { setError('Upload failed: ' + (e.message || 'Unknown error')); }
    finally { setUploading(false); }
  };

  // Save course
  const saveCourse = async () => {
    if (!courseForm.title.trim()) { setError('Title required'); return; }
    if (!courseForm.subject.trim()) { setError('Subject required'); return; }
    setSaving(true); setError('');
    try {
      const t = teachers.find(t => t.id === courseForm.teacher_id);
      const data = {
        ...courseForm,
        institution_id:         institution.id,
        institution_account_id: account.id,
        teacher_name:           t?.full_name || '',
        target_year:            courseForm.target_year ? Number(courseForm.target_year) : null,
        target_class_id:        courseForm.target_class_id || null,
        teacher_id:             courseForm.teacher_id || null,
      };
      if (editCourse) {
        await supabase.from('lms_courses').update(data).eq('id', editCourse.id);
        setSuccess('Course updated!');
      } else {
        await supabase.from('lms_courses').insert(data);
        setSuccess('Course created!');
      }
      await loadInit();
      setShowCourseForm(false); setEditCourse(null); setCourseForm(emptyCourse);
      setTimeout(() => setSuccess(''), 3000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Save chapter
  const saveChapter = async () => {
    if (!chapterForm.title.trim()) { setError('Chapter title required'); return; }
    setSaving(true); setError('');
    try {
      const data = {
        ...chapterForm,
        course_id:      selCourse.id,
        institution_id: institution.id,
        order_index:    editChapter ? chapterForm.order_index : chapters.length,
      };
      if (editChapter) {
        await supabase.from('lms_course_chapters').update(data).eq('id', editChapter.id);
      } else {
        await supabase.from('lms_course_chapters').insert(data);
      }
      setSuccess('Chapter saved!');
      await loadCourseDetail(selCourse);
      setShowChapterForm(false); setEditChapter(null); setChapterForm(emptyChapter);
      setTimeout(() => setSuccess(''), 3000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Save material
  const saveMaterial = async () => {
    if (!materialForm.title.trim()) { setError('Material title required'); return; }
    if (!materialForm.file_url && !materialForm.external_url) { setError('Please upload a file or add a link'); return; }
    setSaving(true); setError('');
    try {
      const data = {
        ...materialForm,
        course_id:      selCourse.id,
        chapter_id:     selChapter?.id || null,
        institution_id: institution.id,
        order_index:    materials.filter(m => m.chapter_id === (selChapter?.id || null)).length,
        external_url:   materialForm.material_type === 'link' ? materialForm.external_url : null,
      };
      await supabase.from('lms_course_materials').insert(data);
      setSuccess('Material added!');
      await loadCourseDetail(selCourse);
      setShowMaterialForm(false); setMaterialForm(emptyMaterial); setAddingLink(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm('Delete this course and all its materials?')) return;
    await supabase.from('lms_courses').delete().eq('id', id);
    setCourses(prev => prev.filter(c => c.id !== id));
    if (selCourse?.id === id) { setView('list'); setSelCourse(null); }
  };

  const deleteChapter = async (id) => {
    if (!window.confirm('Delete this chapter? Materials inside will become uncategorized.')) return;
    await supabase.from('lms_course_chapters').delete().eq('id', id);
    await loadCourseDetail(selCourse);
  };

  const deleteMaterial = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    await supabase.from('lms_course_materials').delete().eq('id', id);
    await loadCourseDetail(selCourse);
  };

  const filtered = courses.filter(c =>
    !search.trim() ||
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    c.subject.toLowerCase().includes(search.toLowerCase()) ||
    (c.teacher_name||'').toLowerCase().includes(search.toLowerCase())
  );

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };
  const F   = ({ label, children, col }) => (
    <div style={{ gridColumn: col||'span 1' }}>
      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );

  // ── COURSE LIST VIEW ────────────────────────────────
  if (view === 'list') return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Courses</h1>
          <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>{courses.length} courses — upload PDFs, videos, images and more</p>
        </div>
        <button onClick={() => { setShowCourseForm(true); setEditCourse(null); setCourseForm(emptyCourse); setError(''); }}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          <Plus size={16} /> New Course
        </button>
      </div>

      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}<button onClick={()=>setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer', color:'#6B7280' }}>✕</button></div>}

      {/* Search */}
      <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:14, marginBottom:20 }}>
        <div style={{ position:'relative' }}>
          <Search size={15} color="#9CA3AF" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search courses, subjects, teachers..."
            style={{ ...inp, paddingLeft:32 }} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:48, color:'#9CA3AF' }}>Loading courses...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
          <BookOpen size={48} color="#E5E7EB" style={{ marginBottom:12 }} />
          <div style={{ fontSize:16, fontWeight:700, color:'#374151', marginBottom:6 }}>No courses yet</div>
          <div style={{ fontSize:13, color:'#9CA3AF', marginBottom:20 }}>Create your first course and upload study materials</div>
          <button onClick={() => { setShowCourseForm(true); setCourseForm(emptyCourse); }}
            style={{ padding:'10px 24px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:'pointer' }}>
            + Create Course
          </button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
          {filtered.map(course => (
            <div key={course.id} style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden', cursor:'pointer' }}>
              {/* Thumbnail / Cover */}
              <div onClick={() => loadCourseDetail(course)}
                style={{ height:120, background: course.thumbnail_url ? `url(${course.thumbnail_url}) center/cover` : `linear-gradient(135deg, ${course.cover_color}, ${course.cover_color}99)`, position:'relative', display:'flex', alignItems:'flex-end', padding:'12px 14px' }}>
                {!course.thumbnail_url && (
                  <div style={{ fontSize:32, marginBottom:4 }}>📚</div>
                )}
                <div style={{ position:'absolute', top:10, right:10, display:'flex', gap:6 }}>
                  {!course.is_published && <span style={{ fontSize:10, fontWeight:800, color:'#92400E', background:'#FEF3C7', padding:'2px 8px', borderRadius:4 }}>Draft</span>}
                  {course.is_featured && <span style={{ fontSize:10, fontWeight:800, color:'#065F46', background:'#D1FAE5', padding:'2px 8px', borderRadius:4 }}>⭐ Featured</span>}
                </div>
              </div>
              <div style={{ padding:'14px 16px' }}>
                <div onClick={() => loadCourseDetail(course)}>
                  <div style={{ fontSize:11, fontWeight:700, color:course.cover_color, marginBottom:4 }}>{course.subject}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:'#111827', marginBottom:4 }}>{course.title}</div>
                  {course.description && <div style={{ fontSize:12, color:'#6B7280', marginBottom:8, lineHeight:1.4 }}>{course.description.slice(0,80)}{course.description.length>80?'...':''}</div>}
                  <div style={{ display:'flex', gap:10, fontSize:12, color:'#9CA3AF', marginBottom:10 }}>
                    {course.teacher_name && <span>👤 {course.teacher_name}</span>}
                    <span>📁 {course.chapters_count} chapters</span>
                    <span>📄 {course.materials_count} files</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => loadCourseDetail(course)}
                    style={{ flex:1, padding:'8px 0', borderRadius:8, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer', fontSize:12, fontWeight:600, color:'#374151', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                    <Eye size={13} /> Manage
                  </button>
                  <button onClick={() => { setEditCourse(course); setCourseForm({...emptyCourse,...course,target_year:course.target_year||'',teacher_id:course.teacher_id||'',target_class_id:course.target_class_id||''}); setShowCourseForm(true); }}
                    style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer' }}>
                    <Edit size={13} color="#374151" />
                  </button>
                  <button onClick={() => deleteCourse(course.id)}
                    style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                    <Trash2 size={13} color="#EF4444" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Course Form Modal */}
      {showCourseForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:620, maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>{editCourse ? 'Edit Course' : 'Create New Course'}</h3>
              <button onClick={() => { setShowCourseForm(false); setEditCourse(null); setCourseForm(emptyCourse); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}</div>}

              {/* Thumbnail upload */}
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>Course Thumbnail (optional)</label>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <div style={{ width:100, height:60, borderRadius:10, background: courseForm.thumbnail_url ? `url(${courseForm.thumbnail_url}) center/cover` : `linear-gradient(135deg,${courseForm.cover_color},${courseForm.cover_color}99)`, border:'1px solid #E5E7EB', flexShrink:0 }} />
                  <div>
                    <input ref={thumbRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => e.target.files?.[0] && uploadThumbnail(e.target.files[0])} />
                    <button onClick={() => thumbRef.current?.click()}
                      style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #BFDBFE', background:'#EFF6FF', color:'#1D4ED8', fontSize:12, fontWeight:600, cursor:'pointer', marginBottom:6, display:'block' }}>
                      Upload Thumbnail
                    </button>
                    <div style={{ fontSize:11, color:'#9CA3AF' }}>JPG, PNG, WebP recommended</div>
                  </div>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <F label="Course Title *" col="span 2">
                  <input value={courseForm.title} onChange={e=>setCourseForm({...courseForm,title:e.target.value})} placeholder="e.g. Introduction to Data Structures" style={inp} />
                </F>
                <F label="Subject *">
                  <input value={courseForm.subject} onChange={e=>setCourseForm({...courseForm,subject:e.target.value})} placeholder="e.g. Data Structures" style={inp} />
                </F>
                <F label="Subject Code">
                  <input value={courseForm.subject_code} onChange={e=>setCourseForm({...courseForm,subject_code:e.target.value})} placeholder="e.g. CS-301" style={inp} />
                </F>
                <F label="Teacher">
                  <select value={courseForm.teacher_id} onChange={e=>setCourseForm({...courseForm,teacher_id:e.target.value})} style={inp}>
                    <option value="">No Teacher Assigned</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select>
                </F>
                <F label="Target Class">
                  <select value={courseForm.target_class_id} onChange={e=>setCourseForm({...courseForm,target_class_id:e.target.value})} style={inp}>
                    <option value="">All Classes</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </F>
                <F label="Target Degree">
                  <select value={courseForm.target_degree} onChange={e=>setCourseForm({...courseForm,target_degree:e.target.value})} style={inp}>
                    <option value="all">All Students</option>
                    {['BS','BE','BBA','MS','MBA','PhD','FSc','ICS'].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </F>
                <F label="Target Year">
                  <select value={courseForm.target_year} onChange={e=>setCourseForm({...courseForm,target_year:e.target.value})} style={inp}>
                    <option value="">All Years</option>
                    {[1,2,3,4,5,6].map(y => <option key={y} value={y}>Year {y}</option>)}
                  </select>
                </F>
                <F label="Description" col="span 2">
                  <textarea value={courseForm.description} onChange={e=>setCourseForm({...courseForm,description:e.target.value})} rows={3} placeholder="Course description..." style={{ ...inp, resize:'vertical' }} />
                </F>
                <F label="Cover Color" col="span 2">
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {COVER_COLORS.map(c => (
                      <div key={c} onClick={() => setCourseForm({...courseForm,cover_color:c})}
                        style={{ width:28, height:28, borderRadius:'50%', background:c, cursor:'pointer', border: courseForm.cover_color===c ? '3px solid #111827' : '3px solid transparent' }} />
                    ))}
                  </div>
                </F>
                <div style={{ gridColumn:'span 2', display:'flex', gap:16 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>
                    <input type="checkbox" checked={courseForm.is_published} onChange={e=>setCourseForm({...courseForm,is_published:e.target.checked})} style={{ width:16, height:16 }} />
                    Publish (visible to students)
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>
                    <input type="checkbox" checked={courseForm.is_featured} onChange={e=>setCourseForm({...courseForm,is_featured:e.target.checked})} style={{ width:16, height:16 }} />
                    ⭐ Featured Course
                  </label>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={() => { setShowCourseForm(false); setEditCourse(null); setCourseForm(emptyCourse); }}
                  style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveCourse} disabled={saving}
                  style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:saving?'wait':'pointer', opacity:saving?0.7:1 }}>
                  {saving ? 'Saving...' : editCourse ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );

  // ── COURSE DETAIL VIEW ──────────────────────────────
  return (
    <LMSLayout>
      {/* Back + header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={() => { setView('list'); setSelCourse(null); }}
          style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151', display:'flex', alignItems:'center', gap:6 }}>
          ← Back
        </button>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#111827', margin:0 }}>{selCourse?.title}</h1>
          <div style={{ fontSize:13, color:'#6B7280', marginTop:2 }}>
            {selCourse?.subject}{selCourse?.teacher_name ? ` · ${selCourse.teacher_name}` : ''} · {chapters.length} chapters · {materials.length} files
          </div>
        </div>
        <button onClick={() => { setShowChapterForm(true); setEditChapter(null); setChapterForm({...emptyChapter, order_index:chapters.length}); setError(''); }}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1px solid #E5E7EB', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#374151' }}>
          <Plus size={14} /> Add Chapter
        </button>
        <button onClick={() => { setShowMaterialForm(true); setSelChapter(null); setMaterialForm(emptyMaterial); setAddingLink(false); setError(''); }}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', fontSize:13, fontWeight:700, cursor:'pointer', color:'#fff' }}>
          <Upload size={14} /> Upload Material
        </button>
      </div>

      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}<button onClick={()=>setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer', color:'#6B7280' }}>✕</button></div>}

      {/* Uncategorized materials */}
      {materials.filter(m => !m.chapter_id).length > 0 && (
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden', marginBottom:16 }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontWeight:700, color:'#374151', fontSize:14 }}>📂 Uncategorized Materials</div>
            <button onClick={() => { setShowMaterialForm(true); setSelChapter(null); setMaterialForm(emptyMaterial); setAddingLink(false); setError(''); }}
              style={{ fontSize:12, color:'#4F8EF7', fontWeight:600, background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
              <Plus size={12} /> Add
            </button>
          </div>
          <MaterialsList materials={materials.filter(m => !m.chapter_id)} onDelete={deleteMaterial} />
        </div>
      )}

      {/* Chapters with materials */}
      {chapters.length === 0 && materials.filter(m => !m.chapter_id).length === 0 ? (
        <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📚</div>
          <div style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>No content yet</div>
          <div style={{ fontSize:13, color:'#9CA3AF', marginBottom:20 }}>Add chapters to organize content, or upload materials directly</div>
          <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
            <button onClick={() => { setShowChapterForm(true); setChapterForm(emptyChapter); }}
              style={{ padding:'9px 18px', borderRadius:10, border:'1px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer', fontSize:13 }}>
              + Add Chapter
            </button>
            <button onClick={() => { setShowMaterialForm(true); setMaterialForm(emptyMaterial); }}
              style={{ padding:'9px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
              Upload Material
            </button>
          </div>
        </div>
      ) : (
        chapters.map((ch, ci) => (
          <div key={ch.id} style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden', marginBottom:14 }}>
            <div style={{ padding:'14px 20px', background:'linear-gradient(135deg,#4F8EF710,#7C3AED08)', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#7C3AED', marginBottom:2 }}>CHAPTER {ci+1}</div>
                <div style={{ fontWeight:800, color:'#111827', fontSize:15 }}>{ch.title}</div>
                {ch.description && <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{ch.description}</div>}
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span style={{ fontSize:11, color:'#94A3B8' }}>{materials.filter(m=>m.chapter_id===ch.id).length} files</span>
                <button onClick={() => { setShowMaterialForm(true); setSelChapter(ch); setMaterialForm(emptyMaterial); setAddingLink(false); setError(''); }}
                  style={{ padding:'6px 12px', borderRadius:7, border:'1px solid #BFDBFE', background:'#EFF6FF', color:'#1D4ED8', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                  <Plus size={12} /> Add File
                </button>
                <button onClick={() => { setEditChapter(ch); setChapterForm({title:ch.title,description:ch.description||'',order_index:ch.order_index,is_published:ch.is_published}); setShowChapterForm(true); setError(''); }}
                  style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                  <Edit size={13} color="#374151" />
                </button>
                <button onClick={() => deleteChapter(ch.id)}
                  style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                  <Trash2 size={13} color="#EF4444" />
                </button>
              </div>
            </div>
            <MaterialsList materials={materials.filter(m => m.chapter_id === ch.id)} onDelete={deleteMaterial} />
          </div>
        ))
      )}

      {/* Chapter Form Modal */}
      {showChapterForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:480 }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <h3 style={{ margin:0, fontWeight:800, color:'#111827' }}>{editChapter ? 'Edit Chapter' : 'Add Chapter'}</h3>
              <button onClick={() => { setShowChapterForm(false); setEditChapter(null); setChapterForm(emptyChapter); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Chapter Title *</label>
              <input value={chapterForm.title} onChange={e=>setChapterForm({...chapterForm,title:e.target.value})} placeholder="e.g. Chapter 1: Introduction" style={{ ...inp, marginBottom:14 }} />
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Description (optional)</label>
              <textarea value={chapterForm.description} onChange={e=>setChapterForm({...chapterForm,description:e.target.value})} rows={2} placeholder="Brief description..." style={{ ...inp, resize:'vertical', marginBottom:16 }} />
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => { setShowChapterForm(false); setEditChapter(null); }}
                  style={{ flex:1, padding:'11px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveChapter} disabled={saving}
                  style={{ flex:2, padding:'11px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:saving?'wait':'pointer', opacity:saving?0.7:1 }}>
                  {saving ? 'Saving...' : editChapter ? 'Update Chapter' : 'Add Chapter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material Upload Modal */}
      {showMaterialForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:540, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <div>
                <h3 style={{ margin:0, fontWeight:800, color:'#111827' }}>Upload Material</h3>
                {selChapter && <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>Adding to: {selChapter.title}</div>}
              </div>
              <button onClick={() => { setShowMaterialForm(false); setMaterialForm(emptyMaterial); setAddingLink(false); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}

              {/* File type tabs */}
              <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                <button onClick={() => setAddingLink(false)}
                  style={{ flex:1, padding:'9px 0', borderRadius:8, border:`2px solid ${!addingLink ? '#4F8EF7' : '#E5E7EB'}`, background: !addingLink ? '#EFF6FF' : '#fff', color: !addingLink ? '#1D4ED8' : '#374151', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <Upload size={14} /> Upload File
                </button>
                <button onClick={() => { setAddingLink(true); setMaterialForm(f => ({...f, material_type:'link'})); }}
                  style={{ flex:1, padding:'9px 0', borderRadius:8, border:`2px solid ${addingLink ? '#4F8EF7' : '#E5E7EB'}`, background: addingLink ? '#EFF6FF' : '#fff', color: addingLink ? '#1D4ED8' : '#374151', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <ExternalLink size={14} /> Add Link
                </button>
              </div>

              {!addingLink ? (
                /* File upload */
                materialForm.file_url ? (
                  <div style={{ background:'#F0FDF4', border:'1.5px solid #BBF7D0', borderRadius:12, padding:'14px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ fontSize:28 }}>{materialForm.material_type === 'pdf' ? '📄' : materialForm.material_type === 'video' ? '🎬' : materialForm.material_type === 'image' ? '🖼️' : '📁'}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, color:'#065F46', fontSize:13 }}>File uploaded ✓</div>
                      <div style={{ fontSize:12, color:'#6B7280' }}>{materialForm.file_name} ({formatSize(materialForm.file_size)})</div>
                      <div style={{ fontSize:11, color:'#10B981', marginTop:2 }}>{materialForm.material_type.toUpperCase()}</div>
                    </div>
                    <button onClick={() => setMaterialForm(f => ({...f, file_url:'', file_name:'', file_size:0, material_type:'other'}))}
                      style={{ background:'none', border:'none', cursor:'pointer' }}><X size={16} color="#6B7280" /></button>
                  </div>
                ) : (
                  <div style={{ marginBottom:16 }}>
                    <input ref={fileRef} type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.jpg,.jpeg,.png,.gif,.webp,.svg,.txt,.zip,.rar"
                      style={{ display:'none' }}
                      onChange={e => e.target.files?.[0] && uploadMaterialFile(e.target.files[0])} />
                    <label style={{ display:'block', cursor:'pointer' }} onClick={() => fileRef.current?.click()}>
                      <div style={{ border:'2px dashed #BFDBFE', borderRadius:12, padding:'32px 20px', textAlign:'center', background:'#F0F9FF' }}>
                        {uploading ? (
                          <div>
                            <div style={{ fontSize:14, fontWeight:600, color:'#3B82F6', marginBottom:8 }}>Uploading... {uploadPct}%</div>
                            <div style={{ background:'#BFDBFE', borderRadius:6, height:6, overflow:'hidden' }}>
                              <div style={{ background:'#3B82F6', height:'100%', width:`${uploadPct}%`, transition:'width 0.3s' }} />
                            </div>
                          </div>
                        ) : (
                          <>
                            <Upload size={32} color="#3B82F6" style={{ margin:'0 auto 10px' }} />
                            <div style={{ color:'#1D4ED8', fontWeight:700, fontSize:14, marginBottom:4 }}>Click to upload file</div>
                            <div style={{ color:'#6B7280', fontSize:12 }}>PDF, Word, PowerPoint, Excel, Video, Images, Audio</div>
                            <div style={{ color:'#9CA3AF', fontSize:11, marginTop:4 }}>All formats supported · Max 500MB</div>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                )
              ) : (
                /* Link input */
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>External URL *</label>
                  <input value={materialForm.external_url} onChange={e=>setMaterialForm({...materialForm,external_url:e.target.value})}
                    placeholder="https://youtube.com/watch?v=... or any URL"
                    style={{ ...inp, marginBottom:6 }} />
                  <div style={{ fontSize:11, color:'#9CA3AF' }}>YouTube videos, Google Drive, OneDrive, or any web link</div>
                </div>
              )}

              {/* Material details */}
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Title *</label>
                <input value={materialForm.title} onChange={e=>setMaterialForm({...materialForm,title:e.target.value})} placeholder="e.g. Lecture 1 - Introduction slides" style={inp} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Description (optional)</label>
                <textarea value={materialForm.description} onChange={e=>setMaterialForm({...materialForm,description:e.target.value})} rows={2} placeholder="Brief description..." style={{ ...inp, resize:'vertical' }} />
              </div>
              <div style={{ display:'flex', gap:12, marginBottom:16 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>
                  <input type="checkbox" checked={materialForm.is_published} onChange={e=>setMaterialForm({...materialForm,is_published:e.target.checked})} style={{ width:15, height:15 }} />
                  Publish immediately
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>
                  <input type="checkbox" checked={materialForm.is_downloadable} onChange={e=>setMaterialForm({...materialForm,is_downloadable:e.target.checked})} style={{ width:15, height:15 }} />
                  Allow download
                </label>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => { setShowMaterialForm(false); setMaterialForm(emptyMaterial); setAddingLink(false); }}
                  style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveMaterial} disabled={saving || uploading}
                  style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:saving||uploading?'wait':'pointer', opacity:saving||uploading?0.7:1 }}>
                  {saving ? 'Saving...' : uploading ? 'Uploading...' : 'Add Material'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );
}

// Materials list component
function MaterialsList({ materials, onDelete }) {
  if (materials.length === 0) return (
    <div style={{ padding:'16px 20px', fontSize:13, color:'#D1D5DB', fontStyle:'italic' }}>No files yet — click "Add File" to upload</div>
  );
  return (
    <div>
      {materials.map((m, i) => {
        const mtype = m.material_type || 'other';
        const color = TYPE_COLORS[mtype] || '#94A3B8';
        const icon  = TYPE_ICONS[mtype] || <File size={18} color="#94A3B8" />;
        return (
          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 20px', borderBottom: i < materials.length-1 ? '1px solid #F9FAFB' : 'none' }}>
            <div style={{ width:38, height:38, borderRadius:10, background:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {icon}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, color:'#111827', fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.title}</div>
              <div style={{ display:'flex', gap:10, fontSize:11, color:'#9CA3AF', marginTop:2 }}>
                <span style={{ color, fontWeight:700 }}>{mtype.toUpperCase()}</span>
                {m.file_name && <span>{m.file_name}</span>}
                {m.file_size > 0 && <span>{(m.file_size/1048576).toFixed(1)}MB</span>}
                <span>👁 {m.views_count} views</span>
                {!m.is_published && <span style={{ color:'#F59E0B' }}>Draft</span>}
                {!m.is_downloadable && <span style={{ color:'#9CA3AF' }}>No download</span>}
              </div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {(m.file_url || m.external_url) && (
                <a href={m.file_url || m.external_url} target="_blank" rel="noreferrer"
                  style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #BFDBFE', background:'#EFF6FF', color:'#1D4ED8', fontSize:12, fontWeight:600, textDecoration:'none', display:'flex', alignItems:'center', gap:4 }}>
                  <Eye size={13} /> Preview
                </a>
              )}
              <button onClick={() => onDelete(m.id)}
                style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                <Trash2 size={13} color="#EF4444" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}